import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Modal, Radio, Switch } from "antd";
import TopicSelect from "./TopicSelect";
import styles from "./style.module.scss";

const { TextArea } = Input;

export const TRIGGER_MAX = 300;
export const RESPONSE_MAX = 1000;
export const TOPIC_MAX = 60;

const EMPTY = { kind: "answer", topic: "Genel", trigger: "", response: "", after_action: "continue", active: true };

// Adds or edits one knowledge base record: a ready answer (the guest says X → the agent says Y)
// or a topic guideline (how to handle a topic while it is discussed).
const KnowledgeRecordModal = ({ open, record, topics, saving, onCancel, onSave }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setValues(record ? { ...EMPTY, ...record } : EMPTY);
      setError("");
    }
  }, [open, record]);

  const patch = (changes) => {
    setValues((prev) => ({ ...prev, ...changes }));
    setError("");
  };

  const submit = () => {
    if (values.kind === "answer" && !values.trigger.trim()) {
      setError(t("surveySettings.knowledge.error_trigger"));
      return;
    }
    if (!values.response.trim()) {
      setError(t("surveySettings.knowledge.error_response"));
      return;
    }
    onSave({
      kind: values.kind,
      topic: values.topic.trim() || "Genel",
      trigger: values.kind === "answer" ? values.trigger.trim() : "",
      response: values.response.trim(),
      after_action: values.after_action,
      active: values.active,
    });
  };

  const isAnswer = values.kind === "answer";
  return (
    <Modal
      open={open}
      title={t(record ? "surveySettings.knowledge.edit_title" : "surveySettings.knowledge.add_title")}
      okText={t("surveySettings.knowledge.save")}
      cancelText={t("surveySettings.knowledge.cancel")}
      confirmLoading={saving}
      onOk={submit}
      onCancel={onCancel}
      destroyOnClose
      width={600}
    >
      <div className={styles.modalField}>
        <label>{t("surveySettings.knowledge.kind")}</label>
        <Radio.Group value={values.kind} onChange={(e) => patch({ kind: e.target.value })}>
          <Radio.Button value="answer">{t("surveySettings.knowledge.kind_answer")}</Radio.Button>
          <Radio.Button value="guideline">{t("surveySettings.knowledge.kind_guideline")}</Radio.Button>
        </Radio.Group>
      </div>
      <div className={styles.modalField}>
        <label>{t("surveySettings.knowledge.topic")}</label>
        <TopicSelect
          value={values.topic}
          onChange={(topic) => patch({ topic })}
          topics={topics}
          maxLength={TOPIC_MAX}
          className={styles.fullWidth}
        />
        <p className={styles.intro}>
          {t(isAnswer ? "surveySettings.knowledge.topic_hint_answer" : "surveySettings.knowledge.topic_hint_guideline")}
        </p>
      </div>
      {isAnswer ? (
        <div className={styles.modalField}>
          <label>{t("surveySettings.knowledge.trigger")}</label>
          <TextArea
            value={values.trigger}
            onChange={(e) => patch({ trigger: e.target.value })}
            placeholder={t("surveySettings.knowledge.trigger_ph")}
            autoSize={{ minRows: 1, maxRows: 3 }}
            maxLength={TRIGGER_MAX}
          />
        </div>
      ) : null}
      <div className={styles.modalField}>
        <label>{t(isAnswer ? "surveySettings.knowledge.response" : "surveySettings.knowledge.guideline")}</label>
        <TextArea
          value={values.response}
          onChange={(e) => patch({ response: e.target.value })}
          placeholder={t(isAnswer ? "surveySettings.knowledge.response_ph" : "surveySettings.knowledge.guideline_ph")}
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={RESPONSE_MAX}
          showCount
        />
      </div>
      {isAnswer ? (
        <div className={styles.modalField}>
          <label>{t("surveySettings.knowledge.after")}</label>
          <Radio.Group value={values.after_action} onChange={(e) => patch({ after_action: e.target.value })}>
            <Radio value="continue">{t("surveySettings.knowledge.after_continue")}</Radio>
            <Radio value="end_call">{t("surveySettings.knowledge.after_end_call")}</Radio>
          </Radio.Group>
        </div>
      ) : null}
      <div className={styles.modalField}>
        <Switch checked={values.active} onChange={(active) => patch({ active })} />{" "}
        {t(values.active ? "surveySettings.knowledge.active" : "surveySettings.knowledge.inactive")}
      </div>
      {error ? <p className={styles.formError}>{error}</p> : null}
    </Modal>
  );
};

export default KnowledgeRecordModal;
